/*
 * Wrist Pinnacle / Find Your Starting Point
 * Inclusive-luxury rule: guide without diagnosing, restricting, storing personal data, or inventing eligibility.
 * Design rule: a lot understood in as few words as possible—with intention.
 */
(function () {
  'use strict';

  var modelOrder = ['facet-01', 'atria-02', 'vale-03', 'meridian-04', 'meridian-05', 'pulse-06', 'coda-07'];
  var modelMeta = {
    'facet-01': { name: 'Facet 01', family: 'Integrated Series', proof: 'NH35 automatic · three-hand date · fixed angular integrated architecture', tradeoff: 'No GMT or chronograph function. Structure is its strongest visual signal.' },
    'atria-02': { name: 'Atria 02', family: 'Integrated Series', proof: 'NH35 automatic · three-hand date · smooth round integrated architecture', tradeoff: 'No GMT or chronograph function. Round familiarity is its strongest form signal.' },
    'vale-03': { name: 'Vale 03', family: 'Integrated Series', proof: 'NH35 automatic · clean no-date layout · smooth round integrated architecture', tradeoff: 'No date, GMT, or chronograph function by design.' },
    'meridian-04': { name: 'Meridian 04', family: 'Meridian Series', proof: 'Automatic GMT architecture · second-time reference · date · 24-hour scale', tradeoff: 'Exact installed caliber, setting direction, and bezel action remain unit-specific.' },
    'meridian-05': { name: 'Meridian 05', family: 'Meridian Series', proof: 'NH35 automatic · three-hand date · elapsed-time visual scale · sport case', tradeoff: 'No GMT or chronograph. Exact bezel action remains unit-specific.' },
    'pulse-06': { name: 'Pulse 06', family: 'Pulse Series', proof: 'VK63 battery-powered chronograph · small seconds · 24-hour indication · tachymeter scale', tradeoff: 'Not an automatic movement. Bracelet or strap is Colorway-specific.' },
    'coda-07': { name: 'Coda 07', family: 'Round Date Series', proof: 'NH35 automatic · three-hand date · fixed smooth round bezel · black strap', tradeoff: 'No GMT or chronograph function. The current Coda Colorways use a black strap.' }
  };
  var broad = {};
  modelOrder.forEach(function (handle) { broad[handle] = 1; });

  var coreQuestions = [
    { id: 'purpose', eyebrow: 'Signal 01 / Use', prompt: 'What would make the watch useful before it makes it impressive?', answers: [
      { id: 'date', label: 'Keep the date in view', note: 'A daily calendar stays on the dial.', weights: { 'facet-01': 8, 'atria-02': 8, 'meridian-04': 3, 'meridian-05': 7 }, reason: 'you wanted the date to remain visible' },
      { id: 'essentials', label: 'Keep only the essentials', note: 'Time remains; the date steps away.', weights: { 'vale-03': 10, 'atria-02': 1 }, reason: 'you preferred time without an added date display' },
      { id: 'places', label: 'Keep another place within reach', note: 'A second-time reference matters.', weights: { 'meridian-04': 12 }, reason: 'you wanted a second-time reference' },
      { id: 'measure', label: 'Measure a moment when it matters', note: 'Timing or measurement stays close.', weights: { 'pulse-06': 12, 'meridian-05': 3 }, reason: 'you wanted timing or measurement to stay close' },
      { id: 'open', label: 'I am still finding out', note: 'Keep all current directions open.', weights: broad, reason: 'you kept the function open' }
    ] },
    { id: 'presence', eyebrow: 'Signal 02 / Presence', prompt: 'How should the watch enter a room?', answers: [
      { id: 'quiet', label: 'Quietly', note: 'Present without taking over.', weights: { 'atria-02': 5, 'vale-03': 6 }, reason: 'you preferred quiet visual presence' },
      { id: 'composed', label: 'With composure', note: 'Balanced, certain, and adaptable.', weights: { 'atria-02': 5, 'facet-01': 2, 'meridian-05': 4 }, reason: 'you preferred a balanced, composed presence' },
      { id: 'structure', label: 'Through structure', note: 'The architecture should be visible.', weights: { 'facet-01': 7, 'meridian-04': 3 }, reason: 'you were drawn to visible structure' },
      { id: 'energy', label: 'With energy', note: 'Scales, registers, or sport form can speak.', weights: { 'meridian-04': 3, 'meridian-05': 3, 'pulse-06': 7 }, reason: 'you preferred an energetic or technical presence' },
      { id: 'open', label: 'No preference yet', note: 'Let function lead first.', weights: broad, reason: 'you kept visual presence open' }
    ] },
    { id: 'form', eyebrow: 'Signal 03 / Form', prompt: 'Which form holds your attention first?', answers: [
      { id: 'integrated', label: 'Case and bracelet as one line', note: 'The transition matters as much as the dial.', weights: { 'facet-01': 6, 'atria-02': 6, 'vale-03': 6 }, reason: 'you noticed an integrated case-and-bracelet line' },
      { id: 'round', label: 'A familiar round form', note: 'Recognition, refined through proportion.', weights: { 'atria-02': 7, 'vale-03': 6 }, reason: 'you preferred a familiar round form' },
      { id: 'sport', label: 'A sport case with a useful scale', note: 'The outer architecture should carry information.', weights: { 'meridian-04': 6, 'meridian-05': 7 }, reason: 'you preferred sport architecture and a visible scale' },
      { id: 'registers', label: 'A dial with several things happening', note: 'Multiple displays create the character.', weights: { 'pulse-06': 9, 'meridian-04': 2 }, reason: 'you were drawn to a multi-information dial' },
      { id: 'open', label: 'I would rather compare them', note: 'Show the forms before deciding.', weights: broad, reason: 'you kept the architecture open' }
    ] },
    { id: 'context', eyebrow: 'Signal 04 / Context', prompt: 'Where will the watch move with you most often?', answers: [
      { id: 'daily', label: 'Through an ordinary day', note: 'Work, errands, meals, and the spaces between.', weights: { 'facet-01': 4, 'atria-02': 5, 'vale-03': 5, 'meridian-05': 3 }, reason: 'you pictured the watch moving through an ordinary day' },
      { id: 'travel', label: 'Between places or time zones', note: 'Another place remains relevant.', weights: { 'meridian-04': 9 }, reason: 'you pictured time across places or time zones' },
      { id: 'active', label: 'Through active routines or timed moments', note: 'Movement, intervals, or measured moments matter.', weights: { 'meridian-05': 5, 'pulse-06': 7 }, reason: 'you wanted an active or timed-moment path' },
      { id: 'occasion', label: 'Into occasions where the object can speak', note: 'The watch can carry more visual presence.', weights: { 'facet-01': 6, 'atria-02': 4, 'pulse-06': 4 }, reason: 'you wanted the object to carry more visual presence on occasion' },
      { id: 'gift', label: 'I am choosing for someone else', note: 'Keep several legible directions open.', weights: { 'facet-01': 2, 'atria-02': 4, 'vale-03': 3, 'meridian-04': 2, 'meridian-05': 3, 'pulse-06': 2 }, reason: 'you are choosing for someone else and kept several directions open' }
    ] },
    { id: 'interaction', eyebrow: 'Signal 05 / Interaction', prompt: 'How much interaction do you want from the watch itself?', answers: [
      { id: 'automatic', label: 'The rhythm of an automatic movement', note: 'A mechanical path that responds to wear and winding.', weights: { 'facet-01': 4, 'atria-02': 4, 'vale-03': 4, 'meridian-04': 4, 'meridian-05': 4 }, reason: 'you preferred an automatic movement path' },
      { id: 'battery', label: 'Battery-powered regularity', note: 'A quartz-regulated chronograph path.', weights: { 'pulse-06': 8 }, reason: 'you preferred a battery-powered chronograph path' },
      { id: 'controls', label: 'Hands, scales, or pushers to engage with', note: 'More information or physical controls are welcome.', weights: { 'meridian-04': 5, 'meridian-05': 2, 'pulse-06': 7 }, reason: 'you wanted more information or controls to engage with' },
      { id: 'simple', label: 'Keep the reading simple', note: 'The dial should resolve quickly.', weights: { 'atria-02': 4, 'vale-03': 7 }, reason: 'you wanted the reading to remain simple' },
      { id: 'open', label: 'I do not need to decide that yet', note: 'Keep movement and interaction open.', weights: broad, reason: 'you kept the movement and interaction path open' }
    ] },
    { id: 'relationship', eyebrow: 'Signal 06 / Time', prompt: 'Which sentence comes closest to the way time sits in your life?', answers: [
      { id: 'visible', label: 'I like the day to stay visible.', note: 'The calendar belongs in the daily picture.', weights: { 'facet-01': 4, 'atria-02': 4, 'meridian-04': 2, 'meridian-05': 4 }, reason: 'you liked the day and date to stay visible' },
      { id: 'reduced', label: 'I make room by removing what I do not need.', note: 'Clarity can come through subtraction.', weights: { 'vale-03': 8 }, reason: 'you connected with removing what is not needed' },
      { id: 'connected', label: 'I often think in more than one place.', note: 'Time can hold a connection across distance.', weights: { 'meridian-04': 9 }, reason: 'you thought about time across more than one place' },
      { id: 'marked', label: 'Some moments deserve measuring.', note: 'A particular interval can matter more than the schedule.', weights: { 'meridian-05': 2, 'pulse-06': 8 }, reason: 'you connected with measuring particular moments' },
      { id: 'shape', label: 'I notice shape before specification.', note: 'Form is the first signal you read.', weights: { 'facet-01': 6, 'atria-02': 5, 'pulse-06': 2 }, reason: 'you noticed shape before specification' },
      { id: 'open', label: 'I am still learning what I notice.', note: 'Keep the interpretation broad.', weights: broad, reason: 'you kept the relationship-to-time signal open' }
    ] }
  ];

  var styleQuestions = [
    { id: 'wardrobe', eyebrow: 'Style 01 / Wardrobe', prompt: 'What feels most like you when you get dressed?', answers: [
      { id: 'clean', label: 'Clean lines. Very little interruption.', note: 'The foundation resolves quickly.', modelWeights: { 'vale-03': 3, 'atria-02': 2, 'facet-01': 1 }, colorTokens: ['cool-metal', 'neutral', 'low-contrast'], reason: 'your ordinary wardrobe relies on clean lines and low interruption' },
      { id: 'structured', label: 'Structure I can feel in the shape.', note: 'Cut and line do the work.', modelWeights: { 'facet-01': 3, 'meridian-04': 1, 'meridian-05': 1 }, colorTokens: ['high-contrast', 'cool-metal', 'deep-neutral'], reason: 'you return to visible structure in what you wear' },
      { id: 'textured', label: 'Texture, ease, and pieces that settle in.', note: 'Depth matters more than polish alone.', modelWeights: { 'atria-02': 1, 'vale-03': 1, 'meridian-05': 1 }, colorTokens: ['green', 'brown', 'champagne', 'texture', 'grounded-warm'], reason: 'you value texture, ease, and a lived-in finish' },
      { id: 'utility', label: 'Sport or utility detail—still considered.', note: 'Function can remain visible.', modelWeights: { 'meridian-04': 3, 'meridian-05': 3, 'pulse-06': 3 }, colorTokens: ['cool-metal', 'black', 'blue', 'teal', 'deep-neutral'], reason: 'you prefer considered sport or utility detail' },
      { id: 'varied', label: 'It changes too often to reduce.', note: 'Keep the wardrobe signal open.', modelWeights: {}, colorTokens: [], reason: 'you kept the wardrobe signal open' }
    ] },
    { id: 'occasion_shift', eyebrow: 'Style 02 / Occasion', prompt: 'When the occasion matters, what usually changes?', answers: [
      { id: 'remove', label: 'I remove more than I add.', note: 'Restraint sharpens the result.', modelWeights: { 'vale-03': 2, 'atria-02': 2 }, colorTokens: ['low-contrast', 'neutral', 'light-neutral'], reason: 'important occasions make you remove rather than add' },
      { id: 'warmth', label: 'I introduce warmth.', note: 'Metal or color changes the temperature.', modelWeights: { 'facet-01': 1, 'atria-02': 1, 'pulse-06': 1 }, colorTokens: ['gold', 'rose', 'champagne', 'ivory', 'brown', 'blush', 'warm-metal'], reason: 'you introduce warmth when the occasion matters' },
      { id: 'contrast', label: 'I sharpen the contrast.', note: 'The separation becomes more deliberate.', modelWeights: { 'facet-01': 1, 'meridian-04': 1, 'meridian-05': 1, 'pulse-06': 1 }, colorTokens: ['high-contrast', 'black', 'deep-neutral', 'mixed-metal'], reason: 'you sharpen contrast for important occasions' },
      { id: 'anchor', label: 'One object carries the look.', note: 'The rest can stay quiet.', modelWeights: { 'facet-01': 2, 'pulse-06': 2, 'meridian-04': 1, 'meridian-05': 1 }, colorTokens: ['high-contrast', 'gold', 'rose', 'mixed-metal'], reason: 'you allow one object to carry the look' },
      { id: 'same', label: 'The same foundation—more deliberate.', note: 'Nothing changes category.', modelWeights: {}, colorTokens: ['harmony'], reason: 'you keep the same foundation and make it more deliberate' }
    ] },
    { id: 'color_direction', eyebrow: 'Style 03 / Color', prompt: 'Which color direction do you return to without having to think?', answers: [
      { id: 'cool', label: 'Cool steel, graphite, or ice', note: 'A cooler, quieter register.', modelWeights: {}, colorTokens: ['cool-metal', 'graphite', 'ice', 'slate', 'charcoal', 'cyan'], reason: 'you return to cool metal and cooler neutrals' },
      { id: 'dark', label: 'Black or another deep neutral', note: 'Depth without added color.', modelWeights: {}, colorTokens: ['black', 'onyx', 'charcoal', 'graphite', 'midnight', 'deep-neutral'], reason: 'you return to black or another deep neutral' },
      { id: 'green_blue', label: 'Green, teal, or blue', note: 'Color, held inside the form.', modelWeights: {}, colorTokens: ['green', 'forest', 'teal', 'blue', 'cyan'], reason: 'you return to green, teal, or blue' },
      { id: 'warm', label: 'Gold, rose, champagne, or ivory', note: 'Warmth across metal and dial.', modelWeights: {}, colorTokens: ['gold', 'rose', 'champagne', 'ivory', 'blush', 'warm-metal'], reason: 'you return to warmer metal and color' },
      { id: 'brown', label: 'Brown or another grounded warm tone', note: 'Warmth with more earth in it.', modelWeights: {}, colorTokens: ['brown', 'grounded-warm', 'rose', 'gold', 'champagne'], reason: 'you return to grounded warm tones' },
      { id: 'compare', label: 'Show me the contrast first', note: 'Let the visual slate decide.', modelWeights: {}, colorTokens: ['high-contrast', 'mixed-metal'], reason: 'you prefer to see contrasting directions before deciding' }
    ] },
    { id: 'resolved', eyebrow: 'Style 04 / Resolution', prompt: 'When what you wore last felt complete—what finished it?', shortAnswer: true, answers: [
      { id: 'restraint', label: 'Nothing asked for more attention than it needed.', note: 'The whole look worked through restraint.', modelWeights: { 'vale-03': 3, 'atria-02': 2 }, colorTokens: ['low-contrast', 'neutral'], reason: 'the whole look worked through restraint' },
      { id: 'harmony', label: 'The colors belonged together.', note: 'Harmony finished the decision.', modelWeights: {}, colorTokens: ['harmony', 'low-contrast', 'warm-harmony'], reason: 'the colors felt resolved through harmony' },
      { id: 'structure', label: 'The structure made everything clearer.', note: 'Shape organized the rest.', modelWeights: { 'facet-01': 3, 'meridian-04': 2, 'meridian-05': 1 }, colorTokens: ['high-contrast', 'cool-metal'], reason: 'visible structure made the rest feel clearer' },
      { id: 'one_object', label: 'One object finished the whole thing.', note: 'A single anchor completed it.', modelWeights: { 'facet-01': 2, 'pulse-06': 2, 'meridian-04': 1, 'meridian-05': 1 }, colorTokens: ['high-contrast', 'gold', 'rose', 'mixed-metal'], reason: 'one decisive object completed the look' },
      { id: 'texture', label: 'Texture made it feel lived in.', note: 'It did not feel arranged.', modelWeights: { 'atria-02': 2, 'vale-03': 2, 'pulse-06': 1 }, colorTokens: ['texture', 'green', 'brown', 'grounded-warm'], reason: 'texture made the look feel lived in' },
      { id: 'detail', label: 'One unexpected detail kept it from feeling ordinary.', note: 'A small interruption made the difference.', modelWeights: { 'pulse-06': 2, 'facet-01': 1, 'atria-02': 1 }, colorTokens: ['high-contrast', 'green', 'teal', 'blue', 'blush'], reason: 'one unexpected detail kept the look from feeling ordinary' }
    ] }
  ];

  // Coda fit weights are editorial recommendation choices, not product specifications.
  // Existing six-model weights are unchanged; Coda follows its round/date/strap record.
  var codaCoreWeights = {
    purpose: { date: 8 },
    presence: { quiet: 6, composed: 5 },
    form: { round: 8 },
    context: { daily: 6, occasion: 3, gift: 4 },
    interaction: { automatic: 4, simple: 6 },
    relationship: { visible: 4, shape: 4 }
  };
  var codaStyleWeights = {
    wardrobe: { clean: 3, textured: 2 },
    occasion_shift: { remove: 2, same: 2 },
    resolved: { restraint: 3, texture: 2 }
  };
  coreQuestions.forEach(function (question) {
    question.answers.forEach(function (answer) {
      var weight = (codaCoreWeights[question.id] || {})[answer.id];
      if (weight) answer.weights['coda-07'] = weight;
    });
  });
  styleQuestions.forEach(function (question) {
    question.answers.forEach(function (answer) {
      var weight = (codaStyleWeights[question.id] || {})[answer.id];
      if (weight) answer.modelWeights['coda-07'] = weight;
    });
  });

  var shortAnswerSignals = [
    { id: 'restraint', label: 'Restraint', keywords: ['simple', 'clean', 'minimal', 'quiet', 'subtle', 'less', 'nothing extra'] },
    { id: 'harmony', label: 'Harmony', keywords: ['match', 'matched', 'together', 'coordinated', 'belonged', 'color harmony', 'colour harmony'] },
    { id: 'structure', label: 'Structure', keywords: ['fit', 'cut', 'tailored', 'shape', 'sharp', 'line', 'structured'] },
    { id: 'one_object', label: 'One decisive object', keywords: ['one piece', 'accessory', 'shoes', 'shoe', 'bag', 'jewelry', 'jewellery', 'watch finished'] },
    { id: 'texture', label: 'Texture', keywords: ['texture', 'fabric', 'leather', 'denim', 'knit', 'suede', 'wool'] },
    { id: 'detail', label: 'Unexpected detail', keywords: ['contrast', 'pop', 'unexpected', 'bold detail', 'different', 'small detail'] }
  ];

  var colorways = [
    ['facet-01', 'Onyx / Ice', ['onyx', 'ice', 'deep-neutral', 'light-neutral', 'high-contrast']],
    ['facet-01', 'Slate / Charcoal', ['slate', 'charcoal', 'cool-metal', 'deep-neutral', 'low-contrast', 'neutral']],
    ['facet-01', 'Gold / Ivory', ['gold', 'ivory', 'warm-metal', 'light-neutral', 'warm-harmony', 'harmony']],
    ['facet-01', 'Rose / Midnight', ['rose', 'midnight', 'warm-metal', 'deep-neutral', 'high-contrast']],
    ['facet-01', 'Rose / Blush', ['rose', 'blush', 'warm-metal', 'low-contrast', 'warm-harmony', 'harmony']],
    ['atria-02', 'Steel / Graphite', ['cool-metal', 'graphite', 'deep-neutral', 'low-contrast', 'neutral']],
    ['atria-02', 'Rose / Ice', ['rose', 'ice', 'warm-metal', 'light-neutral', 'high-contrast']],
    ['atria-02', 'Steel / Graphite / Gold / Green', ['cool-metal', 'graphite', 'gold', 'green', 'mixed-metal', 'high-contrast']],
    ['atria-02', 'Rose / Green Radial', ['rose', 'green', 'warm-metal', 'texture', 'high-contrast']],
    ['vale-03', 'Steel / Green', ['cool-metal', 'green', 'harmony']],
    ['vale-03', 'Steel / Forest Relief', ['cool-metal', 'forest', 'green', 'texture', 'grounded-warm']],
    ['vale-03', 'Steel / Cyan', ['cool-metal', 'cyan', 'blue', 'high-contrast']],
    ['meridian-04', 'Gold / Black', ['gold', 'black', 'warm-metal', 'deep-neutral', 'high-contrast']],
    ['meridian-04', 'Steel / Green', ['cool-metal', 'green', 'harmony']],
    ['meridian-04', 'Steel / Teal', ['cool-metal', 'teal', 'blue', 'high-contrast']],
    ['meridian-04', 'Steel / Black 24', ['cool-metal', 'black', 'deep-neutral', 'high-contrast']],
    ['meridian-05', 'Steel / Blue', ['cool-metal', 'blue', 'high-contrast']],
    ['meridian-05', 'Gold / Black', ['gold', 'black', 'warm-metal', 'deep-neutral', 'high-contrast']],
    ['meridian-05', 'Rose / Black', ['rose', 'black', 'warm-metal', 'deep-neutral', 'high-contrast']],
    ['pulse-06', 'Two-Tone / Black', ['mixed-metal', 'black', 'deep-neutral', 'high-contrast']],
    ['pulse-06', 'Gold / Champagne', ['gold', 'champagne', 'warm-metal', 'warm-harmony', 'harmony']],
    ['pulse-06', 'Rose / Black', ['rose', 'black', 'warm-metal', 'deep-neutral', 'high-contrast']],
    ['pulse-06', 'Rose / Brown', ['rose', 'brown', 'warm-metal', 'grounded-warm', 'warm-harmony']]
  ];

  // Shopify-rendered variant buttons are the catalogue. The small historical list
  // above is retained ONLY for its curated style tokens; it never creates a variant.
  function tokensForColorway(modelId, label) {
    var baseLabel = label.replace(/\s*\/\s*(?:black\s+)?(?:strap|bracelet)$/i, '').trim();
    var known = colorways.find(function (entry) {
      return entry[0] === modelId && (entry[1] === label || entry[1] === baseLabel);
    });
    if (known) return known[2].slice();

    // Conservative hints from the exact label, not image analysis or new specifications.
    var parts = label.toLowerCase().split(/\s*\/\s*/).filter(function (part) {
      return !/\b(strap|bracelet)\b/.test(part);
    });
    var metal = parts[0] || '';
    var tones = parts.join(' ');
    var tokens = [];
    function add(token) { if (tokens.indexOf(token) === -1) tokens.push(token); }
    ['onyx', 'ice', 'slate', 'charcoal', 'gold', 'ivory', 'rose', 'midnight', 'blush',
      'graphite', 'green', 'forest', 'cyan', 'teal', 'blue', 'black', 'brown',
      'champagne', 'white', 'navy', 'red', 'yellow'].forEach(function (token) {
      if (new RegExp('\\b' + token + '\\b').test(tones)) add(token);
    });
    if (/steel|silver|slate/.test(metal)) add('cool-metal');
    if (/gold|rose/.test(metal)) add('warm-metal');
    if (/two-tone|two tone/.test(metal)) add('mixed-metal');
    if (/navy/.test(tones)) add('blue');
    if (/black|onyx|charcoal|graphite|midnight|navy/.test(tones)) add('deep-neutral');
    if (/white|ivory|ice/.test(tones)) add('light-neutral');
    if (/brown|champagne/.test(tones)) add('grounded-warm');
    if (/relief|radial/.test(tones)) add('texture');
    if (!/green|forest|cyan|teal|blue|navy|red|yellow|blush|brown/.test(tones)) add('neutral');
    if (tokens.indexOf('deep-neutral') !== -1 && tokens.indexOf('light-neutral') !== -1) add('high-contrast');
    return tokens;
  }

  function currentColorwaysFor(root) {
    var entries = [];
    if (!root) return entries;
    var eligible = eligibleHandles(root);
    root.querySelectorAll('[data-builder-configuration]').forEach(function (configuration) {
      var handle = configuration.dataset.builderConfiguration;
      if (eligible.indexOf(handle) === -1) return;
      configuration.querySelectorAll('[data-builder-variant]').forEach(function (button) {
        var label = button.dataset.builderVariantLabel;
        if (!label || !button.dataset.variantId || !button.dataset.variantUrl) return;
        entries.push([handle, label, tokensForColorway(handle, label)]);
      });
    });
    return entries;
  }

  var photoMaxBytes = 8 * 1024 * 1024;
  var photoMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
  var photoSignalMeta = {
    'deep-neutral': 'Deep tones',
    'light-neutral': 'Light tones',
    'warm-metal': 'Warmer direction',
    'cool-metal': 'Cooler direction',
    'high-contrast': 'Clear contrast',
    'low-contrast': 'Quiet contrast',
    neutral: 'Neutral restraint',
    green: 'Green',
    blue: 'Blue or teal',
    brown: 'Grounded warmth',
    gold: 'Gold or champagne',
    rose: 'Rose warmth'
  };

  function escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  function analyzeRgba(imageData) {
    var pixels = imageData && imageData.data ? imageData.data : [];
    var width = imageData && imageData.width ? imageData.width : 0;
    var height = imageData && imageData.height ? imageData.height : 0;
    var usable = 0;
    var luminanceTotal = 0;
    var luminanceSquared = 0;
    var warm = 0;
    var cool = 0;
    var saturated = 0;
    var hueFamilies = { green: 0, blue: 0, brown: 0, gold: 0, rose: 0 };
    var centerLuminance = 0;
    var centerCount = 0;
    var edgeLuminance = 0;
    var edgeCount = 0;

    for (var index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] < 160) continue;
      var red = pixels[index] / 255;
      var green = pixels[index + 1] / 255;
      var blue = pixels[index + 2] / 255;
      var maximum = Math.max(red, green, blue);
      var minimum = Math.min(red, green, blue);
      var delta = maximum - minimum;
      var saturation = maximum === 0 ? 0 : delta / maximum;
      var luminance = .2126 * red + .7152 * green + .0722 * blue;
      var pixelNumber = index / 4;
      var x = width ? pixelNumber % width : 0;
      var y = width ? Math.floor(pixelNumber / width) : 0;
      var inCenter = width && height && x >= width * .25 && x <= width * .75 && y >= height * .25 && y <= height * .75;

      usable += 1;
      luminanceTotal += luminance;
      luminanceSquared += luminance * luminance;
      if (red - blue > .075) warm += 1;
      if (blue - red > .075) cool += 1;
      if (saturation >= .2) saturated += 1;
      if (inCenter) { centerLuminance += luminance; centerCount += 1; }
      else { edgeLuminance += luminance; edgeCount += 1; }

      if (delta < .0001 || saturation < .16) continue;
      var hue;
      if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
      else if (maximum === green) hue = 60 * (((blue - red) / delta) + 2);
      else hue = 60 * (((red - green) / delta) + 4);
      if (hue < 0) hue += 360;

      if (hue >= 75 && hue < 170) hueFamilies.green += 1;
      else if (hue >= 170 && hue < 260) hueFamilies.blue += 1;
      else if ((hue >= 335 || hue < 15) && luminance > .28) hueFamilies.rose += 1;
      else if (hue >= 15 && hue < 55 && luminance < .48) hueFamilies.brown += 1;
      else if (hue >= 15 && hue < 75) hueFamilies.gold += 1;
    }

    if (!usable) return { usable: 0 };
    var mean = luminanceTotal / usable;
    var variance = Math.max(0, luminanceSquared / usable - mean * mean);
    return {
      usable: usable,
      luminance: mean,
      contrast: Math.sqrt(variance),
      warmShare: warm / usable,
      coolShare: cool / usable,
      saturatedShare: saturated / usable,
      emphasis: centerCount && edgeCount ? Math.abs(centerLuminance / centerCount - edgeLuminance / edgeCount) : 0,
      hueShares: Object.keys(hueFamilies).reduce(function (shares, key) {
        shares[key] = hueFamilies[key] / usable;
        return shares;
      }, {})
    };
  }

  function photoSignalsFromMetrics(metrics) {
    if (!metrics || metrics.usable < 64) return [];
    var candidates = [];
    function add(token, strength) { if (photoSignalMeta[token]) candidates.push({ token: token, strength: strength }); }
    if (metrics.luminance <= .34) add('deep-neutral', .34 - metrics.luminance + .12);
    else if (metrics.luminance >= .68) add('light-neutral', metrics.luminance - .68 + .12);
    if (metrics.contrast >= .2 || metrics.emphasis >= .16) add('high-contrast', Math.max(metrics.contrast - .14, metrics.emphasis - .1));
    else if (metrics.contrast <= .105) add('low-contrast', .105 - metrics.contrast + .1);
    if (metrics.warmShare - metrics.coolShare >= .12) add('warm-metal', metrics.warmShare - metrics.coolShare);
    else if (metrics.coolShare - metrics.warmShare >= .12) add('cool-metal', metrics.coolShare - metrics.warmShare);
    if (metrics.saturatedShare <= .17) add('neutral', .17 - metrics.saturatedShare + .08);
    Object.keys(metrics.hueShares || {}).forEach(function (token) {
      if (metrics.hueShares[token] >= .09) add(token, metrics.hueShares[token]);
    });
    return candidates.sort(function (left, right) { return right.strength - left.strength; }).filter(function (item, index, all) {
      return all.findIndex(function (candidate) { return candidate.token === item.token; }) === index;
    }).slice(0, 4).map(function (item) { return item.token; });
  }

  function canonicalRule(handle) {
    var rules = window.WristPinnacleConfigurationRules;
    return rules && Array.isArray(rules.models) ? rules.models.find(function (model) { return model.id === handle; }) : null;
  }

  function eligibleHandles(root) {
    return modelOrder.filter(function (handle) {
      var rule = canonicalRule(handle);
      if (!rule || !rule.purchaseUrl || !modelMeta[handle]) return false;
      if (!root) return Boolean(Array.isArray(rule.currentColorways) && rule.currentColorways.length);
      var configuration = root.querySelector('[data-builder-configuration="' + handle + '"]');
      return Boolean(configuration && configuration.querySelector('[data-builder-variant]') &&
        root.querySelector('[data-builder-model="' + handle + '"]'));
    });
  }

  function questionById(id) {
    return coreQuestions.concat(styleQuestions).find(function (question) { return question.id === id; });
  }

  function answerById(question, id) {
    return question && question.answers.find(function (answer) { return answer.id === id; });
  }

  function selectedSize() {
    var value = new URLSearchParams(window.location.search).get('size');
    return ['39', '40', '41'].indexOf(value) !== -1 ? value : '40';
  }

  function withSize(url) {
    var parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('size', selectedSize());
    return parsed.pathname + parsed.search;
  }

  function rank(coreAnswers, styleAnswers, root) {
    var scores = {};
    var reasons = {};
    eligibleHandles(root).forEach(function (handle) {
      scores[handle] = 0;
      reasons[handle] = [];
    });
    Object.keys(coreAnswers).forEach(function (questionId) {
      var answer = answerById(questionById(questionId), coreAnswers[questionId]);
      if (!answer) return;
      Object.keys(answer.weights || {}).forEach(function (handle) {
        if (!Object.prototype.hasOwnProperty.call(scores, handle)) return;
        scores[handle] += answer.weights[handle];
        if (answer.weights[handle] >= 3 && reasons[handle].indexOf(answer.reason) === -1) reasons[handle].push(answer.reason);
      });
    });
    Object.keys(styleAnswers).forEach(function (questionId) {
      var answer = answerById(questionById(questionId), styleAnswers[questionId]);
      if (!answer) return;
      Object.keys(answer.modelWeights || {}).forEach(function (handle) {
        if (!Object.prototype.hasOwnProperty.call(scores, handle)) return;
        scores[handle] += Math.min(3, answer.modelWeights[handle]);
        if (answer.modelWeights[handle] >= 2 && reasons[handle].indexOf(answer.reason) === -1) reasons[handle].push(answer.reason);
      });
    });
    return Object.keys(scores).sort(function (left, right) {
      return scores[right] - scores[left] || modelOrder.indexOf(left) - modelOrder.indexOf(right);
    }).map(function (handle) {
      return { handle: handle, score: scores[handle], reasons: reasons[handle] };
    });
  }

  function selectedStyleTokens(styleAnswers, photoTokenWeights) {
    var tokenCounts = {};
    Object.keys(styleAnswers).forEach(function (questionId) {
      var answer = answerById(questionById(questionId), styleAnswers[questionId]);
      if (!answer) return;
      (answer.colorTokens || []).forEach(function (token) { tokenCounts[token] = (tokenCounts[token] || 0) + 1; });
    });
    Object.keys(photoTokenWeights || {}).forEach(function (token) {
      tokenCounts[token] = (tokenCounts[token] || 0) + photoTokenWeights[token];
    });
    return tokenCounts;
  }

  function exactVariantButton(root, modelId, label) {
    var configuration = root.querySelector('[data-builder-configuration="' + modelId + '"]');
    if (!configuration) return null;
    return Array.prototype.slice.call(configuration.querySelectorAll('[data-builder-variant]')).find(function (button) {
      return button.dataset.builderVariantLabel === label;
    }) || null;
  }

  function rankColorways(root, styleAnswers, modelRanking, photoTokenWeights) {
    var tokens = selectedStyleTokens(styleAnswers, photoTokenWeights);
    var bonus = {};
    modelRanking.slice(0, 3).forEach(function (item, index) { bonus[item.handle] = 3 - index; });
    var ranked = currentColorwaysFor(root).map(function (entry, index) {
      var button = exactVariantButton(root, entry[0], entry[1]);
      if (!button) return null;
      var tokenScore = entry[2].reduce(function (total, token) { return total + (tokens[token] || 0); }, 0);
      return { modelId: entry[0], label: entry[1], tokens: entry[2], score: tokenScore * 4 + (bonus[entry[0]] || 0), order: index, button: button };
    }).filter(Boolean).sort(function (left, right) {
      return right.score - left.score || left.order - right.order;
    });
    var selected = [];
    var primaryHandle = modelRanking[0] && modelRanking[0].handle;
    var primary = ranked.find(function (item) { return item.modelId === primaryHandle; });
    if (primary) selected.push(primary);
    while (selected.length < Math.min(3, ranked.length)) {
      var usedModels = selected.map(function (item) { return item.modelId; });
      var next = ranked.find(function (item) { return selected.indexOf(item) === -1 && usedModels.indexOf(item.modelId) === -1; });
      if (!next) next = ranked.find(function (item) { return selected.indexOf(item) === -1; });
      if (!next) break;
      selected.push(next);
    }
    return selected.concat(ranked.filter(function (item) { return selected.indexOf(item) === -1; }));
  }

  function normalizeShortAnswer(value) {
    return String(value || '').slice(0, 160).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function keywordMatches(text, keyword) {
    var escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
    return new RegExp('(^|[^a-z0-9_])' + escaped + '([^a-z0-9_]|$)', 'i').test(text);
  }

  function suggestSignals(value) {
    var normalized = normalizeShortAnswer(value);
    if (!/[a-z0-9]/.test(normalized)) return [];
    return shortAnswerSignals.filter(function (signal) {
      return signal.keywords.some(function (keyword) { return keywordMatches(normalized, keyword); });
    });
  }

  function parseSharedState() {
    var raw = new URLSearchParams(window.location.search).get('path');
    var parsed = { core: {}, style: {} };
    if (!raw) return parsed;
    raw.split(',').forEach(function (record) {
      var parts = record.split(':');
      var question = questionById(parts[0]);
      if (!question || !answerById(question, parts[1])) return;
      if (coreQuestions.indexOf(question) !== -1) parsed.core[parts[0]] = parts[1];
      else parsed.style[parts[0]] = parts[1];
    });
    return parsed;
  }

  function serializeState(coreAnswers, styleAnswers) {
    return coreQuestions.concat(styleQuestions).filter(function (question) {
      return Boolean(coreAnswers[question.id] || styleAnswers[question.id]);
    }).map(function (question) {
      return question.id + ':' + (coreAnswers[question.id] || styleAnswers[question.id]);
    }).join(',');
  }

  function mount(root) {
    if (!root || root.dataset.startingPointBound === 'true') return;
    var requiredRules = window.WristPinnacleConfigurationRules;
    // Do not couple button binding to a fixed number of catalogue models.
    if (!requiredRules || !Array.isArray(requiredRules.models) || eligibleHandles(root).length === 0) return;

    var start = root.querySelector('[data-starting-point-start]');
    var explore = root.querySelector('[data-starting-point-explore]');
    var panel = root.querySelector('[data-starting-point-panel]');
    var form = root.querySelector('[data-starting-point-form]');
    var progress = root.querySelector('[data-starting-point-progress]');
    var questionRoot = root.querySelector('[data-starting-point-question]');
    var back = root.querySelector('[data-starting-point-back]');
    var skip = root.querySelector('[data-starting-point-skip]');
    var next = root.querySelector('[data-starting-point-next]');
    var results = root.querySelector('[data-starting-point-results]');
    var resultsHeading = root.querySelector('[data-starting-point-results-heading]');
    var resultsGrid = root.querySelector('[data-starting-point-results-grid]');
    var useResults = root.querySelector('[data-starting-point-use]');
    var compareAll = root.querySelector('[data-starting-point-compare]');
    var refine = root.querySelector('[data-starting-point-refine]');
    var revise = root.querySelector('[data-starting-point-revise]');
    var reset = root.querySelector('[data-starting-point-reset]');
    var share = root.querySelector('[data-starting-point-share]');
    var shareStatus = root.querySelector('[data-starting-point-share-status]');
    var modelGrid = root.querySelector('[data-starting-point-model-grid]');
    if (![start, panel, form, progress, questionRoot, back, skip, next, results,
      resultsHeading, resultsGrid, useResults, compareAll, refine, revise, reset,
      share, shareStatus].every(Boolean)) return;
    root.dataset.startingPointBound = 'true';

    var shared = parseSharedState();
    var coreAnswers = shared.core;
    var styleAnswers = shared.style;
    var mode = 'core';
    var step = 0;
    var currentRanking = [];
    var shortAnswer = '';
    var photoState = {
      complete: { objectUrl: '', signals: [], confirmed: [], status: '', error: '' },
      off: { objectUrl: '', signals: [], confirmed: [], status: '', error: '' }
    };

    function currentQuestions() { return mode === 'style' ? styleQuestions : coreQuestions; }
    function currentAnswers() { return mode === 'style' ? styleAnswers : coreAnswers; }

    function confirmedPhotoTokens() {
      var weights = {};
      ['complete', 'off'].forEach(function (slot) {
        var direction = slot === 'complete' ? 1.25 : -.75;
        photoState[slot].confirmed.forEach(function (token) {
          weights[token] = (weights[token] || 0) + direction;
        });
      });
      return weights;
    }

    function hasConfirmedPhotoSignal() { return Object.keys(confirmedPhotoTokens()).length > 0; }

    function releasePhoto(slot) {
      if (photoState[slot].objectUrl) URL.revokeObjectURL(photoState[slot].objectUrl);
      photoState[slot] = { objectUrl: '', signals: [], confirmed: [], status: '', error: '' };
    }

    function releaseAllPhotos() {
      releasePhoto('complete');
      releasePhoto('off');
    }

    function photoSlotMarkup(slot, title, copy) {
      var id = 'wp-starting-point-photo-' + slot;
      return '<article class="wp-starting-point__photo" data-starting-point-photo-slot="' + slot + '"><div class="wp-starting-point__photo-head"><div><strong>' + title + '</strong><span>' + copy + '</span></div><input class="wp-starting-point__photo-input" id="' + id + '" type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" data-starting-point-photo-input="' + slot + '"><label for="' + id + '">Choose photo</label></div><div class="wp-starting-point__photo-preview" data-starting-point-photo-preview hidden><img alt="Selected outfit preview"><button type="button" data-starting-point-remove-photo="' + slot + '">Remove</button></div><p class="wp-starting-point__photo-status" data-starting-point-photo-status aria-live="polite">No photo selected.</p><div class="wp-starting-point__photo-suggestions" data-starting-point-photo-suggestions></div></article>';
    }

    function photoMarkup() {
      return '<details class="wp-starting-point__photos"><summary><span>Use an outfit photo</span><small>Optional · stays on this device</small></summary><div class="wp-starting-point__photos-body"><p>More answers make the direction more specific—not more correct. We read color and contrast only. Confirm what feels true.</p><div class="wp-starting-point__photo-grid">' + photoSlotMarkup('complete', 'Felt complete', 'A look whose direction already felt right.') + photoSlotMarkup('off', 'Felt slightly off', 'A direction you would rather not repeat.') + '</div></div></details>';
    }

    function renderPhotoSlot(slot) {
      var card = questionRoot.querySelector('[data-starting-point-photo-slot="' + slot + '"]');
      if (!card) return;
      var state = photoState[slot];
      var preview = card.querySelector('[data-starting-point-photo-preview]');
      var image = preview.querySelector('img');
      var status = card.querySelector('[data-starting-point-photo-status]');
      var suggestions = card.querySelector('[data-starting-point-photo-suggestions]');
      preview.hidden = !state.objectUrl;
      if (state.objectUrl) image.src = state.objectUrl;
      else image.removeAttribute('src');
      status.textContent = state.error || state.status || 'No photo selected.';
      if (!state.signals.length) {
        suggestions.innerHTML = '';
        return;
      }
      suggestions.innerHTML = '<div>' + state.signals.map(function (token) {
        var pressed = state.confirmed.indexOf(token) !== -1;
        var prefix = slot === 'complete' ? 'Keep' : 'Move away from';
        return '<button type="button" data-starting-point-confirm-photo="' + slot + '" data-starting-point-photo-token="' + escapeHtml(token) + '" aria-pressed="' + (pressed ? 'true' : 'false') + '">' + prefix + ': ' + escapeHtml(photoSignalMeta[token]) + '</button>';
      }).join('') + '</div>';
    }

    function renderPhotoState() {
      renderPhotoSlot('complete');
      renderPhotoSlot('off');
    }

    function decodeLocalImage(file, objectUrl) {
      if (typeof window.createImageBitmap === 'function') return window.createImageBitmap(file, { imageOrientation: 'from-image' });
      return new Promise(function (resolve, reject) {
        var image = new Image();
        image.onload = function () { resolve(image); };
        image.onerror = function () { reject(new Error('decode')); };
        image.src = objectUrl;
      });
    }

    async function analyzePhoto(slot, file) {
      releasePhoto(slot);
      if (!file || photoMimeTypes.indexOf(file.type) === -1) {
        photoState[slot].error = 'Choose a JPEG, PNG, or WebP image.';
        renderPhotoSlot(slot);
        return;
      }
      if (file.size > photoMaxBytes) {
        photoState[slot].error = 'Choose an image smaller than 8 MB.';
        renderPhotoSlot(slot);
        return;
      }
      var objectUrl = URL.createObjectURL(file);
      photoState[slot].objectUrl = objectUrl;
      photoState[slot].status = 'Reading color and contrast on this device…';
      renderPhotoSlot(slot);
      var source;
      try {
        source = await decodeLocalImage(file, objectUrl);
        var width = source.width || source.naturalWidth;
        var height = source.height || source.naturalHeight;
        if (!width || !height) throw new Error('decode');
        var scale = Math.min(1, 72 / Math.max(width, height));
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        var context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('canvas');
        context.drawImage(source, 0, 0, canvas.width, canvas.height);
        var signals = photoSignalsFromMetrics(analyzeRgba(context.getImageData(0, 0, canvas.width, canvas.height)));
        photoState[slot].signals = signals;
        photoState[slot].confirmed = [];
        photoState[slot].status = signals.length ? 'Photo read on this device.' : 'No clear direction assumed. Your other answers still lead.';
      } catch (error) {
        releasePhoto(slot);
        photoState[slot].error = 'This image could not be read. Your other answers still work.';
      } finally {
        if (source && typeof source.close === 'function') source.close();
        renderPhotoSlot(slot);
      }
    }

    function clearHighlights() {
      root.querySelectorAll('[data-builder-model]').forEach(function (card) { card.removeAttribute('data-starting-rank'); });
    }

    function openPanel(nextMode) {
      mode = nextMode || 'core';
      step = 0;
      panel.hidden = false;
      results.hidden = true;
      form.hidden = false;
      renderQuestion();
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function shortAnswerMarkup() {
      return '<div class="wp-starting-point__write"><label for="wp-starting-point-words">Say it your way. What finished the look?<small>One sentence is enough. Nothing changes until you confirm a signal.</small></label><textarea id="wp-starting-point-words" data-starting-point-words maxlength="160" rows="2" autocomplete="off" spellcheck="true" placeholder="Optional"></textarea><div class="wp-starting-point__word-meta"><span data-starting-point-character-count>0 / 160</span><button type="button" data-starting-point-read-words>Find the signals</button></div><div class="wp-starting-point__suggestions" data-starting-point-suggestions aria-live="polite"></div></div>';
    }

    function renderQuestion() {
      var sequence = currentQuestions();
      var answers = currentAnswers();
      var question = sequence[step];
      var selected = answers[question.id] || '';
      progress.textContent = (mode === 'style' ? 'Style ' : '') + '0' + (step + 1) + ' / 0' + sequence.length;
      questionRoot.innerHTML = '<fieldset><legend><span>' + escapeHtml(question.eyebrow) + '</span>' + escapeHtml(question.prompt) + '</legend><div class="wp-starting-point__answers">' + question.answers.map(function (answer) {
        return '<label><input type="radio" name="starting-' + escapeHtml(question.id) + '" value="' + escapeHtml(answer.id) + '"' + (answer.id === selected ? ' checked' : '') + '><span><strong>' + escapeHtml(answer.label) + '</strong><small>' + escapeHtml(answer.note) + '</small></span></label>';
      }).join('') + '</div>' + (question.shortAnswer ? shortAnswerMarkup() + photoMarkup() : '') + '</fieldset>';
      back.disabled = step === 0;
      next.textContent = step === sequence.length - 1 ? (mode === 'style' ? 'Refine my directions' : 'See starting points') : 'Continue';
      next.disabled = !selected && !(question.shortAnswer && hasConfirmedPhotoSignal());
      var words = questionRoot.querySelector('[data-starting-point-words]');
      if (words) {
        words.value = shortAnswer;
        syncWordCount();
      }
      if (question.shortAnswer) renderPhotoState();
      (questionRoot.querySelector('input:checked') || questionRoot.querySelector('input'))?.focus();
    }

    function saveCurrentAnswer() {
      var question = currentQuestions()[step];
      var selected = questionRoot.querySelector('input:checked');
      if (selected) currentAnswers()[question.id] = selected.value;
    }

    function syncWordCount() {
      var words = questionRoot.querySelector('[data-starting-point-words]');
      var count = questionRoot.querySelector('[data-starting-point-character-count]');
      if (!words || !count) return;
      shortAnswer = words.value.slice(0, 160);
      count.textContent = shortAnswer.length + ' / 160';
    }

    function renderSuggestions() {
      var container = questionRoot.querySelector('[data-starting-point-suggestions]');
      if (!container) return;
      var suggestions = suggestSignals(shortAnswer);
      if (!suggestions.length) {
        container.innerHTML = '<p>No signal assumed. Choose one above, or skip.</p>';
        return;
      }
      container.innerHTML = '<p>Does one of these say what you meant?</p><div>' + suggestions.map(function (signal) {
        return '<button type="button" data-starting-point-confirm-signal="' + escapeHtml(signal.id) + '">' + escapeHtml(signal.label) + '</button>';
      }).join('') + '</div>';
    }

    function reasonCopy(item) {
      if (!item.reasons.length) return 'You kept several directions open, so this platform remains a useful comparison point.';
      return item.reasons.slice(0, 3).join('; ') + '.';
    }

    function exactMedia(modelId, label) {
      var manifest = window.WristPinnacleCurrentColorwayMedia;
      return manifest && manifest.mediaByModel && manifest.mediaByModel[modelId] ? manifest.mediaByModel[modelId][label] || '' : '';
    }

    function renderColorwayDirections(ranking) {
      if (!Object.keys(styleAnswers).length && !hasConfirmedPhotoSignal()) return '';
      var colorwayRanking = rankColorways(root, styleAnswers, ranking, confirmedPhotoTokens()).slice(0, 3);
      return '<section class="wp-starting-point__colorways" aria-labelledby="wp-starting-point-colorways"><div><p>Exact current Colorways</p><h4 id="wp-starting-point-colorways">Three visual directions to open first.</h4><span>Color refines the result. It does not rewrite the function.</span></div><div class="wp-starting-point__colorway-grid">' + colorwayRanking.map(function (item, index) {
        var media = item.button.dataset.variantMedia || exactMedia(item.modelId, item.label) || '';
        var url = withSize(item.button.dataset.variantUrl || canonicalRule(item.modelId).purchaseUrl);
        return '<a href="' + escapeHtml(url) + '">' + (media ? '<img src="' + escapeHtml(media) + '" alt="' + escapeHtml(modelMeta[item.modelId].name + ' — ' + item.label) + '" loading="lazy">' : '') + '<span>0' + (index + 1) + ' / ' + escapeHtml(modelMeta[item.modelId].name) + '</span><strong>' + escapeHtml(item.label) + '</strong></a>';
      }).join('') + '</div></section>';
    }

    function showResults() {
      var answeredCount = Object.keys(coreAnswers).length;
      form.hidden = true;
      results.hidden = false;
      clearHighlights();
      if (answeredCount === 0 && Object.keys(styleAnswers).length === 0 && !hasConfirmedPhotoSignal()) {
        currentRanking = [];
        resultsHeading.textContent = 'All current directions remain open.';
        resultsGrid.innerHTML = '<div class="wp-starting-point__open-result"><p>No answer fixed the direction—and that is a valid place to begin.</p><p>Explore every current platform below, or revise one signal when something starts to stand out.</p></div>';
        useResults.hidden = true;
        refine.hidden = true;
        share.hidden = true;
        return;
      }
      currentRanking = rank(coreAnswers, styleAnswers, root).slice(0, 3);
      resultsHeading.textContent = Object.keys(styleAnswers).length || hasConfirmedPhotoSignal() ? 'A more complete direction.' : (answeredCount < coreQuestions.length ? 'Broad starting points.' : 'Three directions from what you told us.');
      var roles = ['Closest direction', 'Another way through', 'Worth a look'];
      resultsGrid.innerHTML = currentRanking.map(function (item, index) {
        var meta = modelMeta[item.handle];
        var rule = canonicalRule(item.handle);
        return '<article class="wp-starting-point__result" data-starting-result="' + escapeHtml(item.handle) + '"><span>' + roles[index] + ' / 0' + (index + 1) + '</span><h4>' + escapeHtml(meta.name) + '</h4><p class="wp-starting-point__result-family">' + escapeHtml(meta.family) + '</p><dl><div><dt>Why it appeared</dt><dd>' + escapeHtml(reasonCopy(item)) + '</dd></div><div><dt>Product record</dt><dd>' + escapeHtml(meta.proof) + '</dd></div><div><dt>Keep in mind</dt><dd>' + escapeHtml(meta.tradeoff) + '</dd></div></dl><div class="wp-starting-point__result-actions"><button type="button" data-starting-open-model="' + escapeHtml(item.handle) + '">Open in Studio</button><a href="' + escapeHtml(withSize(rule.purchaseUrl)) + '">Explore current Colorways</a></div></article>';
      }).join('') + renderColorwayDirections(currentRanking);
      useResults.hidden = false;
      refine.hidden = Object.keys(styleAnswers).length > 0 || hasConfirmedPhotoSignal();
      share.hidden = false;
      resultsHeading.focus();
    }

    function clearModelFilters() {
      root.dispatchEvent(new CustomEvent('wp:clear-model-filters'));
    }

    function highlightResults() {
      clearHighlights();
      clearModelFilters();
      currentRanking.forEach(function (item, index) {
        var card = root.querySelector('[data-builder-model="' + item.handle + '"]');
        if (card) card.dataset.startingRank = String(index + 1);
      });
      modelGrid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    start.addEventListener('click', function () { openPanel('core'); });
    root.querySelector('[data-starting-point-style-start]')?.addEventListener('click', function () { openPanel('style'); });
    explore?.addEventListener('click', function () { clearModelFilters(); root.querySelector('.wp-builder__filters')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    questionRoot.addEventListener('change', function (event) {
      if (event.target.matches('[data-starting-point-photo-input]')) {
        var slot = event.target.dataset.startingPointPhotoInput;
        var file = event.target.files && event.target.files[0];
        analyzePhoto(slot, file);
        event.target.value = '';
        return;
      }
      saveCurrentAnswer();
      next.disabled = false;
    });
    questionRoot.addEventListener('input', function (event) { if (event.target.matches('[data-starting-point-words]')) syncWordCount(); });
    questionRoot.addEventListener('click', function (event) {
      var removePhoto = event.target.closest('[data-starting-point-remove-photo]');
      if (removePhoto) {
        releasePhoto(removePhoto.dataset.startingPointRemovePhoto);
        renderPhotoState();
        next.disabled = !questionRoot.querySelector('input:checked') && !hasConfirmedPhotoSignal();
        return;
      }
      var photoSignal = event.target.closest('[data-starting-point-confirm-photo]');
      if (photoSignal) {
        var photoSlot = photoSignal.dataset.startingPointConfirmPhoto;
        var photoToken = photoSignal.dataset.startingPointPhotoToken;
        var confirmed = photoState[photoSlot].confirmed;
        var confirmedIndex = confirmed.indexOf(photoToken);
        if (confirmedIndex === -1) confirmed.push(photoToken);
        else confirmed.splice(confirmedIndex, 1);
        renderPhotoSlot(photoSlot);
        next.disabled = !questionRoot.querySelector('input:checked') && !hasConfirmedPhotoSignal();
        return;
      }
      if (event.target.closest('[data-starting-point-read-words]')) {
        syncWordCount();
        renderSuggestions();
        return;
      }
      var signal = event.target.closest('[data-starting-point-confirm-signal]');
      if (!signal) return;
      var radio = questionRoot.querySelector('input[value="' + signal.dataset.startingPointConfirmSignal + '"]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        signal.closest('[data-starting-point-suggestions]').querySelectorAll('button').forEach(function (button) { button.setAttribute('aria-pressed', button === signal ? 'true' : 'false'); });
      }
    });
    next.addEventListener('click', function () {
      saveCurrentAnswer();
      var sequence = currentQuestions();
      if (step < sequence.length - 1) { step += 1; renderQuestion(); }
      else showResults();
    });
    back.addEventListener('click', function () {
      saveCurrentAnswer();
      if (step > 0) { step -= 1; renderQuestion(); }
      else if (mode === 'style') showResults();
    });
    skip.addEventListener('click', function () {
      delete currentAnswers()[currentQuestions()[step].id];
      var sequence = currentQuestions();
      if (step < sequence.length - 1) { step += 1; renderQuestion(); }
      else showResults();
    });
    refine.addEventListener('click', function () { openPanel('style'); });
    revise.addEventListener('click', function () { openPanel('core'); });
    reset.addEventListener('click', function () {
      coreAnswers = {};
      styleAnswers = {};
      shortAnswer = '';
      releaseAllPhotos();
      clearHighlights();
      var url = new URL(window.location.href);
      url.searchParams.delete('path');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      openPanel('core');
    });
    useResults.addEventListener('click', highlightResults);
    compareAll.addEventListener('click', function () { clearHighlights(); clearModelFilters(); modelGrid?.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    resultsGrid.addEventListener('click', function (event) {
      var button = event.target.closest('[data-starting-open-model]');
      if (!button) return;
      var modelButton = root.querySelector('[data-builder-model="' + button.dataset.startingOpenModel + '"]');
      if (modelButton) {
        if (modelButton.hidden) clearModelFilters();
        modelButton.click();
      }
    });
    share.addEventListener('click', function () {
      var url = new URL(window.location.href);
      var serialized = serializeState(coreAnswers, styleAnswers);
      if (serialized) url.searchParams.set('path', serialized);
      else url.searchParams.delete('path');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      var success = function () { shareStatus.textContent = 'Direction link copied. Your sentence and photos were not included.'; };
      if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url.href).then(success).catch(function () { shareStatus.textContent = 'Direction saved in the page address. Your sentence and photos were not included.'; });
      else shareStatus.textContent = 'Direction saved in the page address. Your sentence and photos were not included.';
    });

    window.addEventListener('pagehide', releaseAllPhotos, { once: true });

    if (Object.keys(coreAnswers).length || Object.keys(styleAnswers).length) { panel.hidden = false; showResults(); }
  }

  function init() { document.querySelectorAll('[data-wp-builder]').forEach(mount); }

  window.WristPinnacleStartingPoint = {
    version: '2026-09-21.studio-repair.1',
    modelOrder: modelOrder.slice(),
    coreQuestions: coreQuestions,
    styleQuestions: styleQuestions,
    colorways: colorways, // Legacy curated signals; use getColorways(root) for current variants.
    getColorways: currentColorwaysFor,
    rank: rank,
    rankColorways: rankColorways,
    suggestSignals: suggestSignals,
    analyzeRgba: analyzeRgba,
    photoSignalsFromMetrics: photoSignalsFromMetrics,
    photoMimeTypes: photoMimeTypes.slice(),
    photoMaxBytes: photoMaxBytes,
    mount: mount
  };

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
